import type { AssetId } from "@repo/caip";

/**
 * The cash a market order on each EVM chain settles in. Keyed by the numeric
 * eip155 chain id; this is the table every EVM-only consumer reads today and
 * it deliberately carries no Solana entry, so a numeric Solana id keeps
 * failing closed in those paths.
 */
export const DEFAULT_EVM_MARKET_ORDER_CASH_ASSET_BY_CHAIN_ID: Readonly<
  Record<number, AssetId>
> = {
  1: "eip155:1/erc20:0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
  56: "eip155:56/erc20:0x55d398326f99059ff775485246999027b3197955",
  143: "eip155:143/erc20:0x754704bc059f8c67012fed69bc8a327a5aafb603",
  4663: "eip155:4663/erc20:0x5fc5360d0400a0fd4f2af552add042d716f1d168",
  8453: "eip155:8453/erc20:0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
  42161: "eip155:42161/erc20:0xaf88d065e77c8cc2239327c5edb3a432268e5831",
};

/** Solana mainnet, by its genesis-hash CAIP-2 reference. */
export const SOLANA_MAINNET_CHAIN_REF =
  "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp" as const;

/** USDC on Solana mainnet: the cash the Investing Account's Swig wallet holds. */
export const SOLANA_MAINNET_MARKET_ORDER_CASH_ASSET_ID: AssetId =
  "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp/spl:EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";

/**
 * VM-neutral view of the same table, keyed by CAIP-2 chain reference, plus
 * Solana mainnet. Consumers that already carry a `chainRef` (trades, funding
 * corridors) read this one; the numeric accessor above stays the EVM path.
 */
export const DEFAULT_MARKET_ORDER_CASH_ASSET_BY_CHAIN_REF: Readonly<
  Record<string, AssetId>
> = {
  ...Object.fromEntries(
    Object.entries(DEFAULT_EVM_MARKET_ORDER_CASH_ASSET_BY_CHAIN_ID).map(
      ([chainId, assetId]) => [`eip155:${chainId}`, assetId],
    ),
  ),
  [SOLANA_MAINNET_CHAIN_REF]: SOLANA_MAINNET_MARKET_ORDER_CASH_ASSET_ID,
};

export const getDefaultMarketOrderCashAssetIdForChain = (
  chainId: number,
): AssetId | null =>
  DEFAULT_EVM_MARKET_ORDER_CASH_ASSET_BY_CHAIN_ID[chainId] ?? null;

export const getDefaultMarketOrderCashAssetIdForChainRef = (
  chainRef: string,
): AssetId | null =>
  DEFAULT_MARKET_ORDER_CASH_ASSET_BY_CHAIN_REF[chainRef] ?? null;
